import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  HomeIcon,
  UserIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

interface HelpCategory {
  id: string;
  title: string;
  icon: React.ForwardRefExoticComponent<any>;
  topics: HelpTopic[];
}

interface HelpTopic {
  id: string;
  title: string;
  content: string;
  tags: string[];
}

const helpCategories: HelpCategory[] = [
  {
    id: 'account',
    title: 'Account & Profile',
    icon: UserIcon,
    topics: [
      {
        id: 'create-account',
        title: 'How to create an account',
        content: `Creating an account on RentSmart.ie is quick and free! Here's how:

1. Click the "Sign Up" button in the top right corner of any page
2. You can sign up using:
   - Email and password
   - Google account (quick sign-up)
   - Facebook account (quick sign-up)
3. Fill in your basic information (name, email, phone number)
4. Verify your email address by clicking the link sent to your inbox
5. Complete your profile with additional details

Note: Email verification is required to create listings and access certain features. You can start browsing properties immediately after signing up, even before email verification.`,
        tags: ['account', 'signup', 'registration', 'email verification']
      },
      {
        id: 'profile-settings',
        title: 'Managing your profile settings',
        content: `You can manage your profile settings by navigating to your account dashboard:

**To Update Your Profile:**
1. Click on your profile icon in the top right corner
2. Select "Dashboard" from the dropdown menu
3. Navigate to "Profile Settings"
4. Update your information:
   - Name and contact details
   - Profile picture
   - Bio and preferences
   - Notification settings
   - Privacy preferences

**Email Verification:**
If you haven't verified your email, you'll see a reminder in your dashboard. Click the "Verify Email" button to resend the verification link.

**Account Security:**
We recommend enabling email notifications for important account activities and keeping your contact information up to date.`,
        tags: ['profile', 'settings', 'account', 'dashboard']
      },
      {
        id: 'identity-verification',
        title: 'Identity verification for landlords',
        content: `Identity verification helps build trust and ensures a safe rental experience for everyone on RentSmart.ie.

**Why Verify Your Identity?**
- Required for landlords to create active listings
- Increases trust with potential tenants
- Reduces fraudulent listings
- Access to premium landlord features

**How to Verify:**
1. Go to your landlord dashboard
2. Click on "Verify Identity" 
3. Upload a valid government-issued ID (passport, driver's license, or national ID card)
4. Take a clear selfie for verification
5. Our team reviews your submission within 24-48 hours

**What Happens After Verification?**
Once approved, you'll receive a "Verified Landlord" badge on your profile and listings, which increases tenant confidence and inquiry rates.`,
        tags: ['verification', 'identity', 'landlord', 'trust']
      },
      {
        id: 'delete-account',
        title: 'How to delete your account',
        content: `If you wish to delete your RentSmart.ie account:

**Before You Delete:**
- Remove or archive all active listings
- Save any important messages or documents
- Complete any ongoing rental agreements
- Note: This action cannot be undone

**Steps to Delete:**
1. Go to your Dashboard
2. Navigate to "Account Settings"
3. Scroll to "Account Management"
4. Click "Delete Account"
5. Confirm your decision

**What Gets Deleted:**
- Your profile information
- All messages and conversations
- Saved searches and bookmarks
- Listing history (after 30-day grace period)

Need help? Contact support@rentsmart.ie before deleting your account.`,
        tags: ['account', 'delete', 'remove', 'close']
      }
    ]
  },
  {
    id: 'listings',
    title: 'Property Listings',
    icon: HomeIcon,
    topics: [
      {
        id: 'create-listing',
        title: 'How to create a listing',
        content: `Creating a property listing on RentSmart.ie is easy and completely free!

**Two Ways to Create a Listing:**

1. **Start with a Template** (Recommended for first-time users)
   - Choose from pre-made templates for common property types
   - Professional descriptions already written
   - Just customize with your details

2. **Create Your Own Listing**
   - Full customization
   - Perfect for unique properties

**Step-by-Step Process:**
1. Go to your dashboard and click "Create Listing"
2. Choose your preferred method (template or custom)
3. Fill in basic information (title, property type)
4. Add property details (price, location, bedrooms, bathrooms, size)
5. Write a compelling description and select amenities
6. Add additional information (available date, BER rating, virtual tour URL)
7. Upload high-quality images (JPEG, PNG, HEIC supported)
8. Add video URL (YouTube) if available
9. Review and submit

**Pro Tips:**
- Use high-quality, well-lit photos
- Include at least 5-10 images showing different angles
- Write detailed descriptions highlighting unique features
- Keep information accurate and up-to-date
- Save as draft if you need to complete later

Your listing will be active for 3 months and can be renewed or edited anytime.`,
        tags: ['listing', 'property', 'create', 'template']
      },
      {
        id: 'edit-listing',
        title: 'Editing and managing your listings',
        content: `You can edit your listings at any time through your landlord dashboard:

**To Edit a Listing:**
1. Go to your Dashboard
2. Find "My Listings" section
3. Click on the listing you want to edit
4. Click "Edit Listing"
5. Make your changes
6. Click "Save Changes"

**What You Can Edit:**
- Title and description
- Price and availability date
- Property details (bedrooms, bathrooms, size)
- Amenities and features
- Images and videos
- BER rating
- Virtual tour URL

**Managing Listing Status:**
- **Active**: Visible to all users
- **Draft**: Saved but not published
- **Expired**: Automatically expires after 3 months

**Renewing Listings:**
Listings expire after 3 months. You'll receive email reminders before expiration. To renew, simply edit the listing and it will automatically extend for another 3 months.

**Deleting a Listing:**
If you need to remove a listing, go to "My Listings" and click "Delete". You'll be asked to confirm before permanent deletion.`,
        tags: ['listing', 'edit', 'update', 'manage', 'delete']
      },
      {
        id: 'draft-listings',
        title: 'Working with draft listings',
        content: `Draft listings allow you to save your progress and complete your listing later:

**Creating a Draft:**
- While creating a listing, click "Save as Draft" at any step
- Your progress is automatically saved
- Available from step 3 onwards in the creation process

**Accessing Your Drafts:**
1. Go to your Landlord Dashboard
2. See "Draft Listings" count in your overview
3. Navigate to "My Listings" 
4. Filter by "Draft" status

**Completing a Draft:**
1. Click on the draft listing
2. Click "Edit" or "Continue"
3. Complete any missing information
4. Change status to "Active" when ready
5. Submit to make it live

**Draft Reminders:**
You'll receive email reminders about incomplete drafts after 7 and 14 days to help you complete your listing.

**Note:** Drafts are private and not visible to potential tenants until you activate them.`,
        tags: ['draft', 'save', 'incomplete', 'progress']
      },
      {
        id: 'listing-images',
        title: 'Uploading and managing property images',
        content: `High-quality images are crucial for attracting potential tenants:

**Image Requirements:**
- Formats: JPEG, PNG, GIF, HEIC (iPhone photos automatically converted)
- Recommended: At least 5-10 images
- Show different rooms and angles
- Use good lighting and clean spaces

**How to Upload:**
1. During listing creation, go to the "Media Upload" step
2. Click "Choose Files" or drag and drop images
3. Select multiple images at once
4. Preview images before submitting
5. Remove any images by clicking the X button

**Image Tips:**
- First image is your cover photo - make it count!
- Include exterior shots
- Show all rooms clearly
- Capture special features (balcony, garden, parking)
- Avoid blurry or dark photos
- No watermarks or text overlays

**iPhone Users:**
HEIC format photos from iPhones are automatically converted to JPEG format for compatibility.

**Storage:**
All images are securely stored in cloud storage and optimized for fast loading across all devices.`,
        tags: ['images', 'photos', 'upload', 'media', 'heic']
      },
      {
        id: 'grouped-listings',
        title: 'Creating grouped listings for multiple units',
        content: `Grouped listings are perfect for landlords with multiple units in the same building or complex:

**When to Use Grouped Listings:**
- Apartment complexes
- Multiple rooms in the same house
- Office buildings with multiple units
- Student accommodations

**How to Create:**
1. On the listing creation page, select "Grouped Listing"
2. Add group details (title, location, property type)
3. Write a general description for the property
4. Add amenities that apply to all units
5. Create individual units with specific prices and titles
6. Upload images for the entire property
7. Review and submit

**Managing Units:**
- Add units by clicking "Add Unit"
- Remove units with "Remove Unit" button
- Each unit has its own title and price
- Shared description and amenities

**Benefits:**
- Easier management of multiple units
- Unified dashboard view
- Comprehensive analytics
- Consistent presentation

Note: Grouped listing feature may require specific permissions. Check with your account settings.`,
        tags: ['grouped', 'multiple', 'units', 'complex', 'rooms']
      }
    ]
  },
  {
    id: 'safety',
    title: 'Safety & Security',
    icon: ShieldCheckIcon,
    topics: [
      {
        id: 'safe-browsing',
        title: 'Safe browsing and viewing tips',
        content: `Your safety is our priority. Follow these tips when browsing and viewing properties:

**When Browsing:**
- Look for verified landlord badges
- Check property view counts and listing age
- Read descriptions carefully for red flags
- Compare similar properties in the area
- Use our bookmark feature to save favorites

**Before Viewing:**
- Always communicate through RentSmart.ie messaging
- Verify the landlord's identity
- Research the property address online
- Check the BER rating and property details
- Ask questions before committing to a viewing

**During Viewings:**
- Bring a friend or family member
- Schedule viewings during daylight hours
- Meet in public spaces first if possible
- Don't share personal financial information
- Take photos/videos for your records
- Trust your instincts

**Red Flags to Watch For:**
- Requests to pay before viewing
- Prices significantly below market rate
- Landlord refuses in-person viewings
- Pressure to pay deposits immediately
- Poor quality photos or vague descriptions
- Landlord avoids answering basic questions

**Report Suspicious Activity:**
If something doesn't feel right, contact our support team immediately at support@rentsmart.ie`,
        tags: ['safety', 'security', 'tips', 'viewing', 'scams']
      },
      {
        id: 'report-issue',
        title: 'How to report an issue or suspicious listing',
        content: `Help us maintain a safe platform by reporting suspicious activity:

**What to Report:**
- Fraudulent listings
- Inappropriate content or photos
- Scam attempts
- Harassment or abusive behavior
- Duplicate listings
- Inaccurate or misleading information
- Identity theft or impersonation

**How to Report:**

**For Listings:**
1. Open the listing you want to report
2. Look for the "Report" button (usually near the listing details)
3. Select the reason for reporting
4. Provide additional details
5. Submit the report

**For Users/Messages:**
1. Go to the message or profile
2. Click "Report User"
3. Choose the violation type
4. Explain the issue
5. Submit

**What Happens Next:**
- Our team reviews all reports within 24 hours
- We may temporarily suspend suspicious accounts
- You'll receive updates on the report status
- Your identity remains confidential
- Serious violations are reported to authorities if necessary

**Emergency Situations:**
If you're in immediate danger, contact local authorities (Gardaí) first at 999 or 112, then report to us.

**Contact Support Directly:**
Email: support@rentsmart.ie
Include: Screenshots, listing ID, and detailed description`,
        tags: ['report', 'safety', 'security', 'fraud', 'scam']
      },
      {
        id: 'verified-landlords',
        title: 'Understanding verified landlord badges',
        content: `Verified landlord badges help you identify trustworthy property owners:

**What is a Verified Landlord?**
A landlord who has completed our identity verification process and had their documents reviewed by our team.

**Verification Process:**
1. Landlord submits government-issued ID
2. Provides a selfie for facial verification
3. Our team reviews within 24-48 hours
4. Badge appears on profile and listings once approved

**What the Badge Means:**
✓ Identity confirmed
✓ Documents verified
✓ Reduced risk of fraud
✓ Increased trustworthiness
✓ Access to premium features

**Benefits for Tenants:**
- Higher confidence in listing authenticity
- Reduced scam risk
- Professional landlord experience
- Better response rates
- Safer transactions

**Note:** While verification increases trust, always practice due diligence:
- Read reviews if available
- Ask questions before viewing
- Use platform messaging
- Never send money before viewing the property
- Report any concerns to our team

Look for the "Verified" badge next to landlord names and on listing cards.`,
        tags: ['verified', 'badge', 'trust', 'identity', 'landlord']
      },
      {
        id: 'data-privacy',
        title: 'Your data and privacy protection',
        content: `RentSmart.ie takes your privacy seriously and complies with GDPR regulations:

**What Data We Collect:**
- Account information (name, email, phone)
- Profile details you provide
- Listing information
- Messages and communications
- Usage analytics and cookies
- Payment information (if applicable)

**How We Use Your Data:**
- Provide and improve our services
- Match tenants with properties
- Send important notifications
- Prevent fraud and abuse
- Analytics and performance monitoring
- Legal compliance

**Your Privacy Rights:**
- Access your data anytime
- Request data corrections
- Download your data
- Delete your account
- Opt-out of marketing emails
- Control cookie preferences

**Data Security:**
- Encrypted data transmission (SSL)
- Secure cloud storage (Firebase)
- Regular security audits
- Limited employee access
- No selling of personal data

**Cookie Consent:**
We use cookies to improve your experience. You can manage cookie preferences through our cookie consent banner or in your account settings.

**Contact Our Privacy Team:**
For privacy questions or concerns, email: privacy@rentsmart.ie

Read our full Privacy Policy and Terms of Service for complete details.`,
        tags: ['privacy', 'data', 'gdpr', 'security', 'cookies']
      }
    ]
  },
  {
    id: 'contracts',
    title: 'Contracts & Documents',
    icon: DocumentTextIcon,
    topics: [
      {
        id: 'lease-agreement',
        title: 'Understanding lease agreements',
        content: `A lease agreement is a legally binding contract between landlord and tenant:

**Key Components:**
- Names and addresses of all parties
- Property address and description
- Rental amount and payment schedule
- Lease duration (fixed-term or periodic)
- Security deposit amount
- Maintenance responsibilities
- Notice periods for termination
- Rules and restrictions

**Types of Tenancies in Ireland:**

**Part 4 Tenancy:**
- Automatic after 6 months
- Provides security of tenure
- Specific termination rules apply

**Fixed-Term Lease:**
- Agreed duration (usually 6-12 months)
- Cannot be terminated early without agreement
- Converts to Part 4 after initial period

**Periodic Tenancy:**
- Week-to-week or month-to-month
- More flexible but less security
- Becomes Part 4 after 6 months

**Before Signing:**
- Read the entire agreement carefully
- Understand all terms and conditions
- Check rent is within RTB-registered limits
- Verify the landlord owns the property
- Ensure deposit protection is mentioned
- Keep a signed copy for your records

**Important Irish Rental Laws:**
- Rent increases limited (once per year, max 2% above inflation in RPZs)
- Deposit cannot exceed one month's rent
- Notice periods based on tenancy length
- Right to refer disputes to RTB

**Resources:**
- Residential Tenancies Board (RTB): www.rtb.ie
- Threshold (Housing Charity): www.threshold.ie
- Citizens Information: www.citizensinformation.ie`,
        tags: ['contract', 'lease', 'agreement', 'rtb', 'tenancy']
      },
      {
        id: 'ber-rating',
        title: 'Building Energy Rating (BER) explained',
        content: `BER (Building Energy Rating) is a legal requirement for all rental properties in Ireland:

**What is BER?**
BER measures the energy efficiency of a property on a scale from A (most efficient) to G (least efficient).

**BER Scale:**
- A1-A3: Excellent (lowest energy costs)
- B1-B3: Very Good
- C1-C3: Good
- D1-D2: Fair
- E1-E2: Poor
- F: Very Poor
- G: Extremely Poor (highest energy costs)

**Why It Matters:**
- Legal requirement for all rental properties
- Indicates energy costs
- Shows environmental impact
- Influences property value
- Required by law to display on listings

**Landlord Requirements:**
- Must have valid BER certificate
- Display BER rating in all advertising
- Provide copy to tenants
- Certificate valid for 10 years
- Properties built after 2005 must have BER

**Minimum Standards:**
Since 2022, rental properties must meet minimum BER rating of D2 or better (with some exemptions).

**Tenant Benefits:**
- Estimate monthly energy bills
- Compare properties objectively
- Choose more efficient homes
- Lower environmental footprint

**Getting a BER Certificate:**
Only qualified BER assessors can issue certificates. Find assessors at www.seai.ie

**On RentSmart.ie:**
All landlords must provide BER rating when creating a listing. Look for this information in the property details section.`,
        tags: ['ber', 'energy', 'rating', 'certificate', 'efficiency']
      },
      {
        id: 'rtb-registration',
        title: 'RTB registration and tenant rights',
        content: `The Residential Tenancies Board (RTB) protects tenant rights and regulates the rental sector in Ireland:

**What is RTB?**
The RTB is the national body that registers tenancies and resolves landlord-tenant disputes.

**RTB Registration:**
- ALL tenancies must be registered with RTB
- Landlord's responsibility to register
- Must be done within one month of tenancy starting
- Costs €90 for registration
- Renewed annually

**Why Registration Matters:**
- Legal protection for both parties
- Required for Part 4 rights
- Evidence of legitimate tenancy
- Access to dispute resolution
- Tax compliance tracking

**Your Rights as a Tenant:**
✓ Safe, livable accommodation
✓ Privacy and quiet enjoyment
✓ Protection from unlawful eviction
✓ Rent certainty (limited increases)
✓ Security of tenure after 6 months
✓ Return of deposit (minus legitimate deductions)
✓ Standards of repair and maintenance
✓ Written rent book or receipts

**Your Responsibilities:**
- Pay rent on time
- Keep property clean and maintained
- Allow access for inspections (with notice)
- Report repairs needed
- Don't damage the property
- Follow lease terms
- Give proper notice before leaving

**Dispute Resolution:**
If you have a dispute with your landlord:
1. Try to resolve directly first
2. Document everything (emails, photos, receipts)
3. Contact Threshold or Citizens Information for advice
4. File a dispute with RTB (€25 fee)
5. RTB mediates or adjudicates

**Resources:**
- RTB Website: www.rtb.ie
- Threshold: 1800 454 454
- Citizens Information: 0818 07 4000

**Check Your Tenancy:**
Verify your tenancy is registered at www.rtb.ie using your address.`,
        tags: ['rtb', 'rights', 'registration', 'tenant', 'legal']
      },
      {
        id: 'deposits',
        title: 'Security deposits and getting your money back',
        content: `Understanding security deposits helps protect your rights and money:

**Deposit Limits:**
In Ireland, security deposits:
- Cannot exceed one month's rent
- Must be returned within a reasonable time after tenancy ends
- Can have legitimate deductions

**When Paying a Deposit:**
- Get a signed receipt immediately
- Document the amount and date
- Clarify deposit return terms
- Note the bank account it's lodged to
- Keep all documentation safe

**Deposit Protection:**
While Ireland doesn't have mandatory deposit protection schemes like the UK, landlords should:
- Keep deposits in a separate bank account
- Provide details of where deposit is held
- Return deposit promptly at end of tenancy

**Move-In Inspection:**
- Complete a detailed inventory
- Take photos/videos of every room
- Note existing damage or issues
- Both parties should sign
- Keep copies for your records

**Legitimate Deductions:**
Landlords can deduct for:
- Unpaid rent
- Damage beyond normal wear and tear
- Cleaning if property left very dirty
- Missing items from inventory
- Breach of lease terms

**Normal Wear and Tear:**
Landlords CANNOT deduct for:
- Faded paint or curtains
- Worn carpets (reasonable use)
- Minor scuffs or marks
- General aging of appliances
- Standard cleaning

**Getting Your Deposit Back:**
1. Give proper notice as per your lease
2. Clean the property thoroughly
3. Repair any damage you caused
4. Return all keys
5. Provide forwarding address
6. Do final inspection with landlord
7. Request deposit back in writing

**Timeline:**
Deposits should be returned within 14-28 days after you vacate, minus any legitimate deductions with itemized receipts.

**If Deposit Not Returned:**
1. Send written request (email/letter)
2. Request itemized deductions
3. Contact Threshold for advice
4. File dispute with RTB
5. Small Claims Court (for amounts under €2,000)

**Documentation is Key:**
Keep everything: receipts, photos, emails, bank statements, inventory reports.`,
        tags: ['deposit', 'security', 'money', 'refund', 'protection']
      }
    ]
  },
  {
    id: 'messaging',
    title: 'Messaging & Communication',
    icon: ChatBubbleLeftRightIcon,
    topics: [
      {
        id: 'messaging-system',
        title: 'Using the RentSmart messaging system',
        content: `Our built-in messaging system helps you communicate safely and efficiently:

**Accessing Messages:**
1. Click the envelope icon in the navigation bar
2. Or go to your Dashboard and click "Messages"
3. View all conversations in one place
4. Conversations organized by property

**Starting a Conversation:**

**For Tenants:**
1. Find a property you're interested in
2. Click "Contact Landlord" on the listing
3. Write your message
4. Click "Send"
5. Conversation appears in your messages

**For Landlords:**
- Respond to tenant inquiries from your dashboard
- View all property inquiries in one place
- Track conversation history

**Message Features:**
- Real-time notifications
- Message history preserved
- Attachment support (coming soon)
- Read receipts
- Organized by property

**Best Practices:**

**For Tenants:**
- Introduce yourself professionally
- Mention specific details about the property
- Ask relevant questions
- Provide your contact information if comfortable
- Be respectful and courteous

**For Landlords:**
- Respond promptly (within 24 hours)
- Answer questions thoroughly
- Be professional and friendly
- Provide additional details as needed
- Schedule viewings through the platform

**Safety Tips:**
✓ Keep all communication on platform initially
✓ Never send money before viewing
✓ Don't share sensitive personal information too early
✓ Report suspicious messages
✓ Screenshot important conversations

**Email Notifications:**
You'll receive email notifications for:
- New messages
- Message replies
- Inquiry notifications (landlords)
- Manage notification settings in your account

**Privacy:**
Your email and phone number are not shared until you choose to share them in messages.`,
        tags: ['messages', 'communication', 'chat', 'inbox']
      },
      {
        id: 'notifications',
        title: 'Managing notifications and alerts',
        content: `Stay informed with customizable notifications:

**Types of Notifications:**

**Email Notifications:**
- New messages
- Inquiry responses
- Listing expiration reminders
- Account verification reminders
- Draft listing reminders
- Property view milestones
- Important platform updates

**In-App Notifications:**
- Real-time message alerts
- System notifications
- Account activity alerts

**Managing Your Preferences:**
1. Go to your Dashboard
2. Click "Settings" or your profile
3. Navigate to "Notification Preferences"
4. Toggle on/off for each type:
   - Messages and inquiries
   - Listing updates
   - Account activity
   - Marketing emails
   - Platform news

**Email Frequency Options:**
- Instant: Receive immediately
- Daily digest: Once per day summary
- Weekly digest: Once per week summary
- Off: No email notifications

**For Landlords:**
**High-Priority Notifications:**
- New tenant inquiries
- Listing expiring soon
- High view count alerts
- Verification status updates

**Draft Reminders:**
- 7 days after saving draft
- 14 days after saving draft
- Helps you complete listings

**For Tenants:**
**Saved Search Alerts:**
- New properties matching your criteria
- Price changes on bookmarked properties
- Saved property updates

**Notification Icons:**
- Red dot: Unread notification
- Number badge: Count of new notifications
- Envelope icon: Messages

**Turning Off Notifications:**
You can unsubscribe from marketing emails anytime using the link at the bottom of any email, or manage all preferences in your account settings.

**Important:**
Critical account security notifications cannot be disabled (e.g., password changes, login alerts).`,
        tags: ['notifications', 'alerts', 'settings', 'email']
      },
      {
        id: 'inquiry-system',
        title: 'Sending and responding to property inquiries',
        content: `Property inquiries help connect tenants with landlords:

**For Tenants - Sending Inquiries:**

**How to Inquire:**
1. Find a property you're interested in
2. Click "Contact Landlord" or "Send Inquiry"
3. Write your message including:
   - Brief introduction
   - Why you're interested
   - Questions about the property
   - Your move-in timeline
   - Any special requirements
4. Submit the inquiry

**Inquiry Tips:**
- Be professional and polite
- Mention specific features you like
- Ask relevant questions
- Provide context about yourself
- State your viewing availability
- Keep it concise but informative

**What to Include:**
✓ Your name and occupation
✓ Number of people moving in
✓ Desired move-in date
✓ Any pets (if applicable)
✓ References available
✓ Questions about the property

**Response Time:**
Most landlords respond within 24-48 hours. If you don't hear back:
- Check your spam folder
- Send a follow-up message
- Try contacting during business hours

**For Landlords - Managing Inquiries:**

**Viewing Inquiries:**
1. Go to your Landlord Dashboard
2. Check "Pending Inquiries" count
3. Click to view all inquiries
4. Organized by property

**Responding Effectively:**
- Reply within 24 hours
- Answer all questions thoroughly
- Provide additional photos if requested
- Suggest viewing times
- Be professional and friendly
- Set clear expectations

**Inquiry Notifications:**
- Email alerts for new inquiries
- In-app notifications
- Dashboard counter updates

**Red Flags to Watch:**
- Requests to pay without viewing
- Unwilling to provide information
- Extremely urgent or pushy
- Asking to move communication off-platform too quickly

**Tracking Inquiries:**
Your dashboard shows:
- Total inquiries received
- Inquiries per listing
- Response status
- Conversation history

**Best Response Template:**
"Hi [Name], thank you for your inquiry about [Property]. I'd be happy to arrange a viewing. The property is available from [Date]. Are you available on [suggested times]? Please let me know if you have any other questions."`,
        tags: ['inquiries', 'contact', 'landlord', 'tenant', 'viewing']
      },
      {
        id: 'auto-apply',
        title: 'Auto-Apply feature for tenants',
        content: `Auto-Apply helps you automatically match with properties that meet your criteria:

**What is Auto-Apply?**
Auto-Apply is a premium feature that:
- Automatically sends your profile to matching properties
- Saves time on manual applications
- Increases your chances of finding a place
- Gets you ahead of other applicants

**How It Works:**
1. Set up your Auto-Apply profile with:
   - Budget range
   - Preferred locations
   - Property type
   - Number of bedrooms
   - Move-in date
   - Additional preferences

2. When a matching property is listed:
   - Your profile is automatically sent to the landlord
   - Landlord receives your pre-filled application
   - You're notified of the match

3. Landlords can review and contact you directly

**Setting Up Auto-Apply:**
1. Go to your Tenant Dashboard
2. Click "Auto-Apply Settings"
3. Complete your profile:
   - Personal information
   - Employment details
   - References
   - Budget and preferences
   - Upload documents (optional)
4. Activate Auto-Apply
5. Set your preferences

**What to Include:**
- Complete profile information
- Professional photo
- Employment verification
- Previous landlord references
- Brief personal statement
- Proof of income (optional)

**Benefits:**
✓ Never miss a listing
✓ First in line for new properties
✓ Save time on applications
✓ Professional presentation
✓ Increased response rate

**Managing Auto-Apply:**
- Pause anytime
- Update preferences
- View matched properties
- Track application status
- Deactivate when you find a place

**For Landlords:**
Receive pre-qualified tenant applications automatically, saving time on screening.

**Privacy:**
Your information is only shared with landlords of properties matching your criteria. You control what information to include.

**Note:**
Auto-Apply may be a premium feature. Check your account type for availability.`,
        tags: ['auto-apply', 'automatic', 'matching', 'tenant', 'feature']
      }
    ]
  },
  {
    id: 'payments',
    title: 'Payments & Pricing',
    icon: BanknotesIcon,
    topics: [
      {
        id: 'platform-fees',
        title: 'Platform fees and pricing',
        content: `RentSmart.ie offers transparent pricing with no hidden fees:

**For Tenants:**
✓ Completely FREE
✓ Browse all listings
✓ Contact landlords
✓ Send unlimited inquiries
✓ Bookmark properties
✓ No membership fees
✓ No commission charges

**For Landlords:**
✓ FREE to create an account
✓ FREE to post listings
✓ FREE for 3 months per listing
✓ No commission on rentals
✓ No setup fees
✓ No hidden charges

**Listing Duration:**
- Each listing is active for 3 months
- Free to renew or extend
- Unlimited active listings
- Free edits and updates

**Premium Features (Coming Soon):**
- Featured listings (top of search results)
- Auto-Apply tenant matching
- Advanced analytics
- Priority support
- Custom branding

**Why Free?**
We believe in making the rental market accessible to everyone. Our mission is to connect landlords and tenants efficiently without adding unnecessary costs to the rental process.

**Future Pricing:**
We're committed to keeping core features free. Any premium features will be optional add-ons with clear pricing.

**No Commissions:**
Unlike other platforms, we don't take a percentage of your rent or charge commission on successful rentals.

**Compare to Other Platforms:**
- Daft.ie: Charges landlords per listing
- Rent.ie: Subscription fees
- RentSmart.ie: FREE for everyone

**Business Model:**
We're building a sustainable platform through optional premium features, not by charging for basic functionality.`,
        tags: ['pricing', 'fees', 'free', 'cost', 'charges']
      },
      {
        id: 'payment-safety',
        title: 'Safe payment practices',
        content: `Protect yourself from scams by following safe payment practices:

**NEVER:**
❌ Pay before viewing the property in person
❌ Send money via wire transfer or Western Union
❌ Pay to "hold" or "reserve" a property sight unseen
❌ Send deposit before signing a lease
❌ Pay in cash without a receipt
❌ Use payment apps for people you haven't met
❌ Pay more than one month's rent as deposit

**ALWAYS:**
✓ View the property in person first
✓ Verify the landlord's identity
✓ Sign a written lease agreement
✓ Pay by traceable method (bank transfer, check)
✓ Get receipts for all payments
✓ Keep records of all transactions
✓ Confirm deposit protection details

**Red Flags:**
🚩 Landlord is "out of the country"
🚩 Price is too good to be true
🚩 Pressure to pay immediately
🚩 Can't meet in person
🚩 Requests unusual payment methods
🚩 Won't provide viewing
🚩 Asks for payment before lease

**Safe Payment Methods:**
1. **Bank Transfer (Recommended)**
   - Traceable
   - Secure
   - Get confirmation
   - Keep records

2. **Check/Cheque**
   - Physical record
   - Bank-verified
   - Can be stopped if needed

3. **In-Person with Receipt**
   - If paying cash
   - Get signed, dated receipt
   - Include property address
   - Landlord details

**When Paying Deposit:**
- Meet landlord in person
- View the property first
- Sign lease agreement
- Get detailed receipt
- Confirm return conditions
- Document property condition

**If Scammed:**
1. Report to Gardaí immediately
2. Contact your bank
3. Report to RentSmart.ie
4. File a complaint with RTB
5. Warn others on social media

**RentSmart.ie's Role:**
- We provide the platform for connection
- We don't handle payments directly
- We verify landlord identities
- We remove fraudulent listings
- We provide safety guidelines

**Remember:**
Legitimate landlords will understand your need for caution and will follow proper procedures.

**Resources:**
- An Garda Síochána: 999/112
- RTB: www.rtb.ie
- Threshold: 1800 454 454`,
        tags: ['payment', 'safety', 'scams', 'fraud', 'security']
      },
      {
        id: 'rent-payments',
        title: 'Setting up rent payments',
        content: `Guidelines for setting up regular rent payments in Ireland:

**Payment Methods:**
Most landlords in Ireland accept:
1. **Standing Order (Most Common)**
   - Set up through your bank
   - Automatic monthly payment
   - You control the payment
   - Can cancel or modify anytime

2. **Direct Debit**
   - Landlord initiates payment
   - Amount can vary
   - Landlord controls payment
   - Less common for rent

3. **Bank Transfer**
   - Manual payment each month
   - Most flexible
   - Requires remembering each month
   - Get receipt each time

4. **Cash (Not Recommended)**
   - No paper trail
   - Risk of disputes
   - Always get signed receipt

**Setting Up Standing Order:**
1. Get landlord's bank details:
   - Account name
   - IBAN
   - BIC/SWIFT code
   - Bank name
2. Contact your bank (online or branch)
3. Set up standing order:
   - Amount (monthly rent)
   - Frequency (monthly)
   - Start date
   - Reference (your name + address)
4. Get confirmation

**Payment Schedule:**
- Typically due on the 1st of each month
- Some landlords prefer different dates
- Agree in writing in your lease
- Set up payment a day or two early

**Payment Reference:**
Always include:
- Your name
- Property address
- Month paying for
Example: "John Smith, Apt 4, 123 Main St, Jan 2025"

**Getting Receipts:**
- Request receipt for every payment
- Include: date, amount, period covered, landlord signature
- Keep all receipts for tax purposes
- Digital receipts are acceptable

**Rent Book:**
Landlords must provide a rent book showing:
- All payments received
- Dates of payment
- Outstanding balance
- Landlord details

**What if You Can't Pay:**
1. Contact landlord immediately
2. Explain situation
3. Propose payment plan
4. Get agreement in writing
5. Contact Threshold for advice
6. Apply for rent supplement if eligible

**Late Payment:**
- Late fees must be reasonable
- Check your lease terms
- Landlord can issue notice for non-payment
- Multiple missed payments can lead to eviction

**Tax Benefits:**
- Rent paid is not tax-deductible for tenants
- Keep records for rent supplement applications
- May be eligible for Rent Tax Credit (up to €500)

**Proof of Rent:**
Needed for:
- Rent supplement applications
- Tax credit claims
- Credit applications
- Visa applications
Keep all payment records!`,
        tags: ['rent', 'payment', 'standing order', 'transfer']
      }
    ]
  }
];

const HelpCenterPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<HelpTopic | null>(null);

  // Format content with proper styling
  const formatContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip empty lines
      if (line.trim() === '') {
        continue;
      }

      // Headers (lines with **text:** format at start)
      if (line.match(/^\*\*[^*]+:\*\*/)) {
        const text = line.replace(/^\*\*([^*]+):\*\*/, '$1:');
        elements.push(
          <h3 key={key++} className="text-lg font-bold text-gray-900 mt-6 mb-3">
            {text}
          </h3>
        );
      }
      // Bold headers (lines with just **text**)
      else if (line.match(/^\*\*[^*]+\*\*$/)) {
        const text = line.replace(/^\*\*([^*]+)\*\*$/, '$1');
        elements.push(
          <h4
            key={key++}
            className="text-base font-semibold text-gray-900 mt-4 mb-2"
          >
            {text}
          </h4>
        );
      }
      // Numbered lists
      else if (line.match(/^\d+\.\s/)) {
        const items = [];
        while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
          const itemText = lines[i].replace(/^\d+\.\s/, '');
          items.push(<li key={key++}>{formatInlineText(itemText)}</li>);
          i++;
        }
        i--; // Step back one since loop will increment
        elements.push(
          <ol
            key={key++}
            className="list-decimal list-inside space-y-2 ml-4 mb-4"
          >
            {items}
          </ol>
        );
      }
      // Bullet lists (starting with - or •)
      else if (line.match(/^[-•]\s/)) {
        const items = [];
        while (i < lines.length && lines[i].match(/^[-•]\s/)) {
          const itemText = lines[i].replace(/^[-•]\s/, '');
          items.push(<li key={key++}>{formatInlineText(itemText)}</li>);
          i++;
        }
        i--; // Step back one since loop will increment
        elements.push(
          <ul key={key++} className="list-disc list-inside space-y-2 ml-4 mb-4">
            {items}
          </ul>
        );
      }
      // Checkmark/X lists (✓ or ❌ or ✗ or 🚩)
      else if (line.match(/^[✓❌✗🚩]/)) {
        const symbol = line[0];
        const text = line.substring(1).trim();
        const colorClass =
          symbol === '✓'
            ? 'text-green-700'
            : symbol === '❌' || symbol === '✗'
              ? 'text-red-700'
              : symbol === '🚩'
                ? 'text-orange-700'
                : 'text-gray-700';

        elements.push(
          <div
            key={key++}
            className={`flex items-start space-x-2 mb-2 ${colorClass}`}
          >
            <span className="flex-shrink-0 font-bold">{symbol}</span>
            <span>{formatInlineText(text)}</span>
          </div>
        );
      }
      // Regular paragraphs
      else {
        elements.push(
          <p key={key++} className="mb-4 leading-relaxed">
            {formatInlineText(line)}
          </p>
        );
      }
    }

    return elements;
  };

  // Format inline text (bold, links, etc.)
  const formatInlineText = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let key = 0;

    // Match **bold** text
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(
        <strong key={`bold-${key++}`} className="font-semibold text-gray-900">
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return helpCategories;

    return helpCategories
      .map((category) => ({
        ...category,
        topics: category.topics.filter(
          (topic) =>
            topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            topic.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
            topic.tags.some((tag) =>
              tag.toLowerCase().includes(searchQuery.toLowerCase())
            )
        )
      }))
      .filter((category) => category.topics.length > 0);
  }, [searchQuery]);

  // SEO Helmet block
  const seoTitle = 'Help Center | RentSmart.ie Support & FAQs';
  const seoDescription =
    'Find answers to common questions and learn how to use RentSmart.ie. Browse help articles, guides, and contact support for rental platform assistance in Ireland.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="help center, support, FAQ, RentSmart, rental platform, Ireland, guides, contact support, how to use"
        />
        <link rel="canonical" href="https://rentsmart.ie/help-center" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/help-center" />
      </Helmet>
      <div className="min-h-screen py-8 sm:py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <QuestionMarkCircleIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-indigo-600" />
            <h1 className="mt-3 sm:mt-4 text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">
              Help Center
            </h1>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-500 px-4">
              Find answers to common questions and learn how to use RentSmart.ie
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Content */}
          <div className="grid md:grid-cols-12 gap-8">
            {/* Categories Sidebar */}
            <div className="md:col-span-4 lg:col-span-3">
              <nav className="space-y-1">
                {filteredCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedTopic(null);
                    }}
                    className={`w-full flex items-center px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md ${
                      selectedCategory === category.id
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <category.icon
                      className={`mr-2 sm:mr-3 flex-shrink-0 h-5 w-5 sm:h-6 sm:w-6 ${
                        selectedCategory === category.id
                          ? 'text-indigo-600'
                          : 'text-gray-400'
                      }`}
                    />
                    <span className="truncate">{category.title}</span>
                    {category.topics.length > 0 && (
                      <span className="ml-auto text-xs text-gray-400">
                        {category.topics.length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Topics & Content */}
            <div className="md:col-span-8 lg:col-span-9">
              {selectedTopic ? (
                // Topic Content View
                <div className="bg-white shadow rounded-lg p-4 sm:p-5 md:p-6">
                  <button
                    onClick={() => setSelectedTopic(null)}
                    className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 mb-3 sm:mb-4 flex items-center"
                  >
                    ← Back to topics
                  </button>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                    {selectedTopic.title}
                  </h2>
                  <div className="prose prose-sm sm:prose max-w-none text-gray-700 space-y-3 sm:space-y-4">
                    {formatContent(selectedTopic.content)}
                  </div>
                </div>
              ) : selectedCategory ? (
                // Topics List View
                <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
                  {filteredCategories
                    .find((cat) => cat.id === selectedCategory)
                    ?.topics.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => setSelectedTopic(topic)}
                        className="w-full text-left p-4 sm:p-5 md:p-6 hover:bg-gray-50 flex items-center justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900">
                            {topic.title}
                          </h3>
                          <p className="mt-1 text-xs sm:text-sm text-gray-500 line-clamp-2">
                            {topic.content}
                          </p>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      </button>
                    ))}
                </div>
              ) : (
                // Category Grid View (when no category is selected)
                <div className="grid gap-6 sm:grid-cols-2">
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className="relative rounded-lg p-4 sm:p-5 md:p-6 bg-white shadow hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center">
                        <category.icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-indigo-600" />
                        <h3 className="ml-2.5 sm:ml-3 text-base sm:text-lg md:text-xl font-medium text-gray-900">
                          {category.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-gray-500">
                        {category.topics.length} articles
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact Support Section */}
          <div className="mt-8 sm:mt-10 md:mt-12 bg-indigo-50 rounded-lg p-5 sm:p-6 md:p-8 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-indigo-900 mb-3 sm:mb-4">
              Still Need Help?
            </h2>
            <p className="text-sm sm:text-base text-indigo-700 mb-4 sm:mb-5 md:mb-6 px-4">
              Can't find what you're looking for? Our support team is here to
              help.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-5 py-2.5 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 no-underline"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpCenterPage;
